import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useChartColors } from "./chartColors";
import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import type { AnalyticsEnvelope, LeaveCoverage, AnalyticsViewProps } from "./types";

type LeaveCoverageData = AnalyticsEnvelope & LeaveCoverage;

export function LeaveCoverageView({
  data,
  loading,
  error,
}: AnalyticsViewProps<LeaveCoverageData>) {
  const { t } = useTranslation();
  const colors = useChartColors();
  const coverage = data?.coverage ?? [];
  return (
    <Panel title={t("homework.analytics.leave.title")}>
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !coverage.length ? (
        <EmptyState message={t("homework.analytics.empty")} />
      ) : (
        <>
          <div role="img" aria-label={t("homework.analytics.leave.title")}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={coverage} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} />
                <YAxis stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} width={36} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="available"
                  stroke={colors.series(2)}
                  fill={colors.series(2)}
                  fillOpacity={0.2}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-0.5 text-[12px]">
            {(data?.upcoming ?? []).slice(0, 8).map((u, i) => (
              <li key={`${u.user_id}-${i}`}>
                {u.name}: {u.start_date} → {u.end_date} ({u.leave_type})
              </li>
            ))}
          </ul>
        </>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
